import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './CategoryManager.css';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedParentSubcategory, setSelectedParentSubcategory] = useState(null);
  const [isManageMode, setIsManageMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    isActive: true
  });
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: '',
    description: '',
    image: '',
    category: '',
    parentSubcategory: '',
    isActive: true
  });
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [uploadingSubcategoryImage, setUploadingSubcategoryImage] = useState(false);
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [subcategoryImageFile, setSubcategoryImageFile] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger',
    itemType: '' // 'category' or 'subcategory'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  // Refetch subcategories when category changes in form to update parent subcategory dropdown
  useEffect(() => {
    if (subcategoryFormData.category) {
      fetchSubcategories();
    }
  }, [subcategoryFormData.category]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId = null) => {
    try {
      // Always fetch all subcategories to build hierarchy
      const response = await axios.get(`${API_URL}/api/subcategories`);
      setSubcategories(response.data.data);
    } catch (error) {
      setSubcategories([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      // Ensure image is included in the request
      const submitData = {
        ...formData,
        image: formData.image || undefined
      };
      
      // Remove empty image field if not set
      if (!submitData.image) {
        delete submitData.image;
      }
      
      if (editingCategory) {
        await axios.put(`${API_URL}/api/categories/${editingCategory._id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/categories`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Category created successfully!');
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', image: '', isActive: true });
      fetchCategories();
    } catch (error) {
      console.error('Category submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
      // Don't close modal on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = async (category) => {
    // Fetch fresh category data to get new pre-signed URLs
    try {
      const response = await axios.get(`${API_URL}/api/categories/${category._id}`);
      const freshCategory = response.data.data;
      
      setEditingCategory(freshCategory);
      setFormData({
        name: freshCategory.name,
        description: freshCategory.description || '',
        image: freshCategory.image || '',
        isActive: freshCategory.isActive
      });
      setCategoryImageFile(null);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load category details');
      console.error('Error fetching category:', error);
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category? All products and subcategories in this category will also be affected. This action cannot be undone.',
      type: 'danger',
      itemType: 'category',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/categories/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Category deleted successfully!');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger', itemType: '' });
          fetchCategories();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Delete failed');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger', itemType: '' });
        }
      }
    });
  };

  const openModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', image: '', isActive: true });
    setCategoryImageFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const openSubcategoryModal = (categoryId = null, parentSubcategoryId = null) => {
    setSelectedCategory(categoryId);
    setSelectedParentSubcategory(parentSubcategoryId);
    setIsManageMode(!!categoryId && !parentSubcategoryId); // Manage mode when category is set but no parent
    setEditingSubcategory(null);
    setSubcategoryFormData({
      name: '',
      description: '',
      image: '',
      category: categoryId || '',
      parentSubcategory: parentSubcategoryId || '',
      isActive: true
    });
    setSubcategoryImageFile(null);
    setShowSubcategoryModal(true);
  };

  const closeSubcategoryModal = () => {
    setShowSubcategoryModal(false);
    setEditingSubcategory(null);
    setSelectedCategory(null);
    setSelectedParentSubcategory(null);
    setIsManageMode(false);
    setSubcategoryImageFile(null);
  };

  const handleSubcategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubcategoryFormData({
      ...subcategoryFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUploadCategoryImage = async () => {
    if (!categoryImageFile) {
      toast.error('Please select an image');
      return;
    }

    setUploadingCategoryImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', categoryImageFile);
    
    // Build query string for upload type and category info
    let queryString = '?uploadType=category';
    if (editingCategory) {
      queryString += `&categoryId=${editingCategory._id}`;
    } else if (formData.name) {
      queryString += `&categoryName=${encodeURIComponent(formData.name)}`;
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload${queryString}`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const imageUrl = response.data.data?.baseS3Url || response.data.data?.url;
      if (!imageUrl) {
        console.error('Upload response missing image URL:', response.data);
        toast.error('Upload succeeded but no image URL returned');
        return;
      }
      
      setFormData({ ...formData, image: imageUrl });
      setCategoryImageFile(null);
      toast.success('Category image uploaded successfully!');
    } catch (error) {
      console.error('Category image upload error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Image upload failed');
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  const handleUploadSubcategoryImage = async () => {
    if (!subcategoryImageFile) {
      toast.error('Please select an image');
      return;
    }

    setUploadingSubcategoryImage(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', subcategoryImageFile);
    
    // Build query string for upload type and subcategory info
    let queryString = '?uploadType=subcategory';
    if (editingSubcategory) {
      queryString += `&subcategoryId=${editingSubcategory._id}`;
    } else {
      // Get category name
      const selectedCategory = categories.find(cat => 
        cat._id === subcategoryFormData.category || 
        cat._id === subcategoryFormData.category?._id
      );
      if (selectedCategory) {
        queryString += `&categoryName=${encodeURIComponent(selectedCategory.name)}`;
      }
      
      if (subcategoryFormData.name) {
        queryString += `&subcategoryName=${encodeURIComponent(subcategoryFormData.name)}`;
      }
      
      // Get parent subcategory name if exists
      if (subcategoryFormData.parentSubcategory) {
        const parentSub = subcategories.find(sub => 
          sub._id === subcategoryFormData.parentSubcategory ||
          sub._id === subcategoryFormData.parentSubcategory?._id
        );
        if (parentSub) {
          queryString += `&parentSubcategoryName=${encodeURIComponent(parentSub.name)}`;
        }
      }
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload${queryString}`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const imageUrl = response.data.data?.baseS3Url || response.data.data?.url;
      if (!imageUrl) {
        console.error('Upload response missing image URL:', response.data);
        toast.error('Upload succeeded but no image URL returned');
        return;
      }
      
      setSubcategoryFormData({ ...subcategoryFormData, image: imageUrl });
      setSubcategoryImageFile(null);
      toast.success('Subcategory image uploaded successfully!');
    } catch (error) {
      console.error('Subcategory image upload error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || 'Image upload failed');
    } finally {
      setUploadingSubcategoryImage(false);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      // Prepare data - convert empty string to null for parentSubcategory
      const submitData = {
        ...subcategoryFormData,
        parentSubcategory: subcategoryFormData.parentSubcategory || null,
        image: subcategoryFormData.image || undefined
      };
      
      // Remove empty image field if not set
      if (!submitData.image) {
        delete submitData.image;
      }
      
      if (editingSubcategory) {
        await axios.put(`${API_URL}/api/subcategories/${editingSubcategory._id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Subcategory updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/subcategories`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Subcategory created successfully!');
      }
      closeSubcategoryModal();
      fetchCategories();
      fetchSubcategories();
    } catch (error) {
      console.error('Subcategory submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Operation failed');
      }
      // Don't close modal on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEditSubcategory = async (subcategory) => {
    // Fetch fresh subcategory data to get new pre-signed URLs and full hierarchy
    try {
      const response = await axios.get(`${API_URL}/api/subcategories/${subcategory._id}`);
      const freshSubcategory = response.data.data;
      
      setEditingSubcategory(freshSubcategory);
      setSubcategoryFormData({
        name: freshSubcategory.name,
        description: freshSubcategory.description || '',
        image: freshSubcategory.image || '',
        category: freshSubcategory.category._id || freshSubcategory.category,
        parentSubcategory: freshSubcategory.parentSubcategory?._id || freshSubcategory.parentSubcategory || '',
        isActive: freshSubcategory.isActive
      });
      setIsManageMode(false);
      setSelectedCategory(null);
      setSelectedParentSubcategory(null);
      setSubcategoryImageFile(null);
      setShowSubcategoryModal(true);
    } catch (error) {
      toast.error('Failed to load subcategory details');
      console.error('Error fetching subcategory:', error);
    }
  };

  const handleDeleteSubcategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this subcategory?')) {
      try {
        await axios.delete(`${API_URL}/api/subcategories/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Subcategory deleted successfully!');
        fetchCategories();
        fetchSubcategories();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Delete failed');
      }
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Category Manager</h1>
        <div>
          <button onClick={openSubcategoryModal} className="btn btn-secondary" style={{ marginRight: '10px' }}>
            + Add Subcategory
          </button>
          <button onClick={openModal} className="btn btn-primary">
            + Add Category
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Image</th>
              <th>Subcategories</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center' }}>
                  No categories found
                </td>
              </tr>
            ) : (
              categories.map((category) => {
                const categorySubcategories = subcategories.filter(
                  sub => sub.category?._id === category._id || sub.category === category._id
                );
                return (
                <tr key={category._id}>
                  <td>{category.name}</td>
                  <td>{category.description || '-'}</td>
                  <td>
                    {category.image ? (
                      <img src={category.image} alt={category.name} className="thumbnail" />
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {categorySubcategories.length} subcategory(ies)
                      </span>
                      {categorySubcategories.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#888' }}>
                          {categorySubcategories.slice(0, 2).map(sub => sub.name).join(', ')}
                          {categorySubcategories.length > 2 && '...'}
                        </div>
                      )}
                      <button
                        onClick={() => openSubcategoryModal(category._id)}
                        className="btn btn-secondary btn-sm"
                        style={{ marginTop: '5px', fontSize: '11px', padding: '4px 8px' }}
                      >
                        Manage
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${category.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleEdit(category)}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(category._id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }} 
              noValidate
            >
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Category Image</label>
                <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#0066cc' }}>📐 Ideal Image Size:</strong>
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    <strong>Category Carousel:</strong> 200 x 200 pixels (1:1 square) - Displayed as circular icons in category carousel<br/>
                    <strong>Featured Categories Grid:</strong> 800 x 256 pixels (3:1 ratio) - Displayed in featured categories section on homepage
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setCategoryImageFile(e.target.files[0]);
                    }
                  }}
                  style={{ marginBottom: '10px' }}
                />
                {categoryImageFile && (
                  <div>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      ✓ {categoryImageFile.name} selected
                    </p>
                    <button
                      type="button"
                      onClick={handleUploadCategoryImage}
                      className="btn btn-primary"
                      disabled={uploadingCategoryImage}
                      style={{ marginRight: '10px' }}
                    >
                      {uploadingCategoryImage ? '⏳ Uploading...' : '⬆️ Upload Image'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCategoryImageFile(null)}
                      className="btn btn-secondary"
                    >
                      Clear
                    </button>
                  </div>
                )}
                {formData.image && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={formData.image} 
                      alt="Category" 
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                      onError={(e) => {
                        console.error('Failed to load category image:', formData.image);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="modal-overlay" onClick={closeSubcategoryModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</h2>
              <button className="modal-close" onClick={closeSubcategoryModal}>×</button>
            </div>
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubcategorySubmit(e);
              }} 
              noValidate
            >
              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={subcategoryFormData.category}
                  onChange={handleSubcategoryInputChange}
                  required
                  disabled={isManageMode}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {isManageMode && (
                  <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                    Creating top-level subcategory for this category
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={subcategoryFormData.description}
                  onChange={handleSubcategoryInputChange}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Parent Subcategory (Optional - for nested subcategories)</label>
                <select
                  name="parentSubcategory"
                  value={subcategoryFormData.parentSubcategory}
                  onChange={handleSubcategoryInputChange}
                  style={{ minHeight: '150px' }}
                  size="8"
                  disabled={isManageMode || !!selectedParentSubcategory}
                >
                  <option value="">None (Top-level subcategory)</option>
                  {(() => {
                    // If parent subcategory is pre-selected, show it as selected
                    if (selectedParentSubcategory) {
                      const parent = subcategories.find(sub => 
                        sub._id === selectedParentSubcategory || 
                        (typeof selectedParentSubcategory === 'object' && sub._id === selectedParentSubcategory._id) ||
                        (typeof selectedParentSubcategory === 'string' && sub._id === selectedParentSubcategory)
                      );
                      if (parent) {
                        return (
                          <option key={parent._id} value={parent._id}>
                            • {parent.name} (Selected)
                          </option>
                        );
                      }
                    }
                    
                    // Filter subcategories by selected category
                    const categorySubs = subcategories.filter(sub => 
                      (sub.category?._id === subcategoryFormData.category || 
                       sub.category === subcategoryFormData.category) &&
                      !sub.parentSubcategory // Only top-level subcategories
                    );
                    
                    // Build hierarchical structure
                    const buildHierarchy = (parentId = null, level = 0) => {
                      const children = subcategories.filter(sub => {
                        const parentMatch = parentId 
                          ? (sub.parentSubcategory?._id === parentId || sub.parentSubcategory === parentId)
                          : false;
                        const categoryMatch = sub.category?._id === subcategoryFormData.category || 
                                            sub.category === subcategoryFormData.category;
                        return parentMatch && categoryMatch;
                      });
                      
                      return children.flatMap(child => {
                        const indent = '  '.repeat(level);
                        const option = (
                          <option key={child._id} value={child._id}>
                            {indent}├─ {child.name}
                          </option>
                        );
                        const nested = buildHierarchy(child._id, level + 1);
                        return [option, ...nested];
                      });
                    };
                    
                    // Show top-level subcategories and their nested children
                    return categorySubs.flatMap(sub => {
                      const indent = '';
                      const option = (
                        <option key={sub._id} value={sub._id}>
                          {indent}• {sub.name}
                        </option>
                      );
                      const nested = buildHierarchy(sub._id, 1);
                      return [option, ...nested];
                    });
                  })()}
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                  {isManageMode 
                    ? 'Creating top-level subcategory only. Use "Create Subcategory" button on a parent subcategory to create nested subcategories.'
                    : selectedParentSubcategory
                    ? 'Creating nested subcategory under the selected parent.'
                    : 'Select a parent subcategory to create a nested subcategory. Top-level subcategories are shown with "•", nested ones with "├─".'}
                </small>
              </div>
              <div className="form-group">
                <label>Subcategory Image</label>
                <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                  <strong style={{ display: 'block', marginBottom: '5px', color: '#0066cc' }}>📐 Ideal Image Size:</strong>
                  <span style={{ fontSize: '13px', color: '#333' }}>
                    <strong>Subcategory Image:</strong> 200 x 200 pixels (1:1 square) - Displayed as circular icons similar to categories
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSubcategoryImageFile(e.target.files[0]);
                    }
                  }}
                  style={{ marginBottom: '10px' }}
                />
                {subcategoryImageFile && (
                  <div>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                      ✓ {subcategoryImageFile.name} selected
                    </p>
                    <button
                      type="button"
                      onClick={handleUploadSubcategoryImage}
                      className="btn btn-primary"
                      disabled={uploadingSubcategoryImage}
                      style={{ marginRight: '10px' }}
                    >
                      {uploadingSubcategoryImage ? '⏳ Uploading...' : '⬆️ Upload Image'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubcategoryImageFile(null)}
                      className="btn btn-secondary"
                    >
                      Clear
                    </button>
                  </div>
                )}
                {subcategoryFormData.image && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={subcategoryFormData.image} 
                      alt="Subcategory" 
                      style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                      onError={(e) => {
                        console.error('Failed to load subcategory image:', subcategoryFormData.image);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={subcategoryFormData.isActive}
                    onChange={handleSubcategoryInputChange}
                  />
                  Active
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={closeSubcategoryModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSubcategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Top-Level Subcategories Table */}
      {(() => {
        const topLevelSubcategories = subcategories.filter(sub => !sub.parentSubcategory);
        return topLevelSubcategories.length > 0 ? (
          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Subcategories</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Image</th>
                  <th>Nested Subcategories</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topLevelSubcategories.map((subcategory) => {
                  const nestedSubcategories = subcategories.filter(
                    sub => (sub.parentSubcategory?._id === subcategory._id || sub.parentSubcategory === subcategory._id)
                  );
                  return (
                    <tr key={subcategory._id}>
                      <td>{subcategory.name}</td>
                      <td>{subcategory.category?.name || '-'}</td>
                      <td>{subcategory.description || '-'}</td>
                      <td>
                        {subcategory.image ? (
                          <img src={subcategory.image} alt={subcategory.name} className="thumbnail" />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {nestedSubcategories.length} nested subcategory(ies)
                          </span>
                          {nestedSubcategories.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              {nestedSubcategories.slice(0, 2).map(sub => sub.name).join(', ')}
                              {nestedSubcategories.length > 2 && '...'}
                            </div>
                          )}
                          <button
                            onClick={() => {
                              const categoryId = subcategory.category?._id || subcategory.category;
                              openSubcategoryModal(categoryId, subcategory._id);
                            }}
                            className="btn btn-primary btn-sm"
                            style={{ marginTop: '5px', fontSize: '11px', padding: '4px 8px' }}
                            title="Create nested subcategory under this parent"
                          >
                            + Add Nested
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${subcategory.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {subcategory.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleEditSubcategory(subcategory)}
                            className="btn btn-secondary btn-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSubcategory(subcategory._id)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null;
      })()}

      {/* Nested Subcategories Table */}
      {(() => {
        const nestedSubcategories = subcategories.filter(sub => sub.parentSubcategory);
        return nestedSubcategories.length > 0 ? (
          <div className="card" style={{ marginTop: '20px' }}>
            <h2>Nested Subcategories</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Parent Subcategory</th>
                  <th>Description</th>
                  <th>Image</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nestedSubcategories.map((subcategory) => (
                  <tr key={subcategory._id}>
                    <td>
                      <span style={{ marginRight: '8px', color: '#999' }}>├─</span>
                      {subcategory.name}
                    </td>
                    <td>{subcategory.category?.name || '-'}</td>
                    <td>{subcategory.parentSubcategory?.name || '-'}</td>
                    <td>{subcategory.description || '-'}</td>
                    <td>
                      {subcategory.image ? (
                        <img src={subcategory.image} alt={subcategory.name} className="thumbnail" />
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`badge ${subcategory.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {subcategory.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEditSubcategory(subcategory)}
                          className="btn btn-secondary btn-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            const categoryId = subcategory.category?._id || subcategory.category;
                            openSubcategoryModal(categoryId, subcategory._id);
                          }}
                          className="btn btn-primary btn-sm"
                          title="Create nested subcategory under this parent"
                        >
                          + Sub
                        </button>
                        <button
                          onClick={() => handleDeleteSubcategory(subcategory._id)}
                          className="btn btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null;
      })()}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger', itemType: '' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default CategoryManager;

